// "Fast start" for MP4/MOV uploads, done in the browser without copying the file.
//
// Many MP4s keep their index (the "moov" box: which bytes hold which frame) at the END of the
// file. The server's space-saver mode turns a film into its streaming copy while it uploads, so
// it has to read the index first; such files used to fail after a few seconds ("couldn't read
// the start of the file"), e.g. a 22 GB 4K WEB-DL on 2026-10-13.
//
// The fix is the standard lossless one (what `qt-faststart` / `-movflags +faststart` do): move the
// index in front of the media data and add its size to every chunk offset inside it. Here the
// new file is a Blob stitched from slices of the original plus the rebuilt index, so a 22 GB file
// costs only the index's few MB of memory. The pictures and sound are untouched.

const CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl', 'edts', 'dinf', 'mvex']);
const MAX_MOOV = 400 << 20; // an index this big isn't a film we can rearrange in a browser
const U32 = 0xffffffff;

interface TopBox {
  type: string;
  start: number;
  size: number; // whole box, header included
}

/** A parsed box: containers keep their children, everything else its raw payload. */
interface Box {
  type: string;
  payload?: Uint8Array; // leaf: bytes after the header
  children?: Box[];
}

/** What fastStart reads: a File/Blob in the browser; tests pass a reader over a file on disk. */
export interface Source {
  size: number;
  read(start: number, end: number): Promise<Uint8Array>;
}
const blobSource = (b: Blob): Source => ({ size: b.size, read: async (s, e) => new Uint8Array(await b.slice(s, e).arrayBuffer()) });
const readAt = (f: Source, start: number, len: number) => f.read(start, Math.min(f.size, start + len));
const typeOf = (b: Uint8Array, i: number) => String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3]);

/** The file's top-level boxes, reading only their headers. */
async function topLevel(f: Source): Promise<TopBox[] | null> {
  const out: TopBox[] = [];
  let pos = 0;
  while (pos < f.size) {
    if (f.size - pos < 8) return null;
    const h = await readAt(f, pos, 16);
    const dv = new DataView(h.buffer);
    let size = dv.getUint32(0);
    const type = typeOf(h, 4);
    if (size === 1) size = Number(dv.getBigUint64(8));
    else if (size === 0) size = f.size - pos;
    if (size < 8 || pos + size > f.size) return null; // not an MP4 we understand: leave it alone
    out.push({ type, start: pos, size });
    pos += size;
  }
  return out;
}

function parse(b: Uint8Array): Box[] {
  const out: Box[] = [];
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let pos = 0;
  while (pos + 8 <= b.length) {
    let size = dv.getUint32(pos);
    const type = typeOf(b, pos + 4);
    let head = 8;
    if (size === 1) {
      size = Number(dv.getBigUint64(pos + 8));
      head = 16;
    } else if (size === 0) size = b.length - pos;
    if (size < head || pos + size > b.length) throw new Error(`bad box ${type}`);
    const body = b.subarray(pos + head, pos + size);
    out.push(CONTAINERS.has(type) ? { type, children: parse(body) } : { type, payload: body });
    pos += size;
  }
  return out;
}

function sizeOf(box: Box): number {
  const body = box.children ? box.children.reduce((n, c) => n + sizeOf(c), 0) : box.payload!.length;
  return body + 8 > U32 ? body + 16 : body + 8;
}

function write(boxes: Box[], out: Uint8Array, at: number): number {
  const dv = new DataView(out.buffer, out.byteOffset, out.byteLength);
  for (const box of boxes) {
    const size = sizeOf(box);
    const big = size > U32;
    dv.setUint32(at, big ? 1 : size);
    for (let i = 0; i < 4; i++) out[at + 4 + i] = box.type.charCodeAt(i);
    let p = at + 8;
    if (big) {
      dv.setBigUint64(p, BigInt(size));
      p += 8;
    }
    if (box.children) p = write(box.children, out, p);
    else {
      out.set(box.payload!, p);
      p += box.payload!.length;
    }
    at = p;
  }
  return at;
}

/** Every chunk-offset table (stco: 32-bit, co64: 64-bit) in the index. */
function offsetTables(boxes: Box[], found: Box[] = []): Box[] {
  for (const b of boxes) {
    if (b.children) offsetTables(b.children, found);
    else if (b.type === 'stco' || b.type === 'co64') found.push(b);
  }
  return found;
}

function readOffsets(b: Box): number[] {
  const p = b.payload!;
  const dv = new DataView(p.buffer, p.byteOffset, p.byteLength);
  const n = dv.getUint32(4);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) out[i] = b.type === 'stco' ? dv.getUint32(8 + i * 4) : Number(dv.getBigUint64(8 + i * 8));
  return out;
}

function writeOffsets(b: Box, offsets: number[], wide: boolean) {
  const p = new Uint8Array(8 + offsets.length * (wide ? 8 : 4));
  p.set(b.payload!.subarray(0, 4)); // version + flags
  const dv = new DataView(p.buffer);
  dv.setUint32(4, offsets.length);
  offsets.forEach((o, i) => (wide ? dv.setBigUint64(8 + i * 8, BigInt(o)) : dv.setUint32(8 + i * 4, o)));
  b.type = wide ? 'co64' : 'stco';
  b.payload = p;
}

/** The new file as pieces: byte ranges of the original, and the rebuilt index. */
export type Piece = { from: number; to: number } | Uint8Array<ArrayBuffer>;

export interface FastStartPlan {
  pieces: Piece[] | null; // null: send the file as it is
  why: string; // for the logs
}

/**
 * How to send a file with its index first, or null pieces when there's nothing to do or it isn't
 * an MP4/MOV this understands. `wideAbove` is for tests: offsets above it switch to 64-bit
 * tables (normally 2^32 − 1, the 32-bit limit).
 */
export async function fastStartPlan(file: Source, wideAbove = U32): Promise<FastStartPlan> {
  const keep = (why: string): FastStartPlan => ({ pieces: null, why });
  const top = await topLevel(file);
  if (!top) return keep('not a box-structured file');
  const moovAt = top.findIndex((b) => b.type === 'moov');
  const firstMdat = top.findIndex((b) => b.type === 'mdat');
  if (moovAt < 0 || firstMdat < 0) return keep('no moov/mdat');
  if (top.some((b) => b.type === 'moof')) return keep('fragmented');
  if (moovAt < firstMdat) return keep('already fast start');
  const moov = top[moovAt];
  if (moov.size > MAX_MOOV) return keep('index too large');

  const insertAt = top[firstMdat].start;
  const moovEnd = moov.start + moov.size;
  const tree = parse(await readAt(file, moov.start, moov.size));
  const tables = offsetTables(tree);
  const original = tables.map(readOffsets);

  // Where each byte ends up: before the first mdat it stays put; between it and the old index it
  // moves down by the new index's size; after the old index it moves by the size difference.
  // The new size depends on which tables must widen to 64 bits, so settle it in a few rounds.
  let newSize = moov.size;
  let settled = false;
  for (let round = 0; round < 8 && !settled; round++) {
    const shift = (o: number) => (o < insertAt ? o : o < moov.start ? o + newSize : o >= moovEnd ? o + newSize - moov.size : o);
    tables.forEach((t, i) => {
      const moved = original[i].map(shift);
      writeOffsets(t, moved, t.type === 'co64' || moved.some((o) => o > wideAbove));
    });
    const size = tree.reduce((n, b) => n + sizeOf(b), 0);
    settled = size === newSize;
    newSize = size;
  }
  if (!settled) return keep('index size did not settle');
  const bytes = new Uint8Array(newSize);
  write(tree, bytes, 0);
  const pieces: Piece[] = [{ from: 0, to: insertAt }, bytes, { from: insertAt, to: moov.start }, { from: moovEnd, to: file.size }];
  return { pieces: pieces.filter((p) => p instanceof Uint8Array || p.to > p.from), why: `index moved to the front (${(moov.size / 1e6).toFixed(1)} MB)` };
}

export interface FastStartResult {
  blob: Blob;
  moved: boolean;
  why: string;
}

/** The file to upload: the same film with its index first (a Blob stitched from slices of the
 * original, nothing copied), or the file itself. */
export async function fastStart(file: Blob): Promise<FastStartResult> {
  const plan = await fastStartPlan(blobSource(file));
  if (!plan.pieces) return { blob: file, moved: false, why: plan.why };
  const parts: BlobPart[] = plan.pieces.map((p) => (p instanceof Uint8Array ? p : file.slice(p.from, p.to)));
  return { blob: new Blob(parts, { type: file.type }), moved: true, why: plan.why };
}
